import type {
  ThgAnnualSummary,
  ThgPayoutFailure,
  ThgPayoutState,
  ThgPayoutSyncResult,
  ThgStationPayout,
} from "../../../types/thgAnnualSummary";
import { PAYOUT_COPY } from "../ThgAnnualSummary.consts";

/**
 * Whether the year carries CLR-283 payout data. False on a provisional year (every `payout` is null) and on a
 * backend without the feature (no `payout` at all): then the page shows no payout UI and exports as before.
 */
export const hasPayoutData = (summary: Pick<ThgAnnualSummary, "stations" | "payoutTotals">): boolean =>
  summary.payoutTotals != null || (summary.stations ?? []).some((station) => station.payout != null);

const STATE_LABEL: Record<ThgPayoutState, string> = {
  NOT_GENERATED: "Not generated",
  DRAFT: "Draft",
  FINAL: "Final",
  CANCELLED: "Cancelled",
  NOTHING_TO_PAY: "Not payable",
  NO_OWNER: "Not payable",
  NO_BANK_DETAILS: "Not payable",
  INTERNAL: "Internal – not paid",
};

const REASON_LABEL: Partial<Record<ThgPayoutState, string>> = {
  NOTHING_TO_PAY: "Nothing to pay",
  NO_OWNER: "No owner",
  NO_BANK_DETAILS: "No bank details on file",
};

/** The 422 `reason`, worded to follow "cannot get a credit note: …". */
const INELIGIBILITY_LABEL: Record<string, string> = {
  NOTHING_TO_PAY: "nothing to pay",
  NO_OWNER: "no owner assigned",
  NO_BANK_DETAILS: "no bank details on file",
};

export const payoutStateLabel = (payout: Pick<ThgStationPayout, "state" | "creating">): string =>
  payout.creating ? "Creating…" : STATE_LABEL[payout.state] ?? payout.state;

/** Why a row cannot be paid, or null when it can. */
export const payoutReasonLabel = (state: ThgPayoutState): string | null => REASON_LABEL[state] ?? null;

export const ineligibilityReasonLabel = (reason: string | null | undefined): string | null =>
  (reason && INELIGIBILITY_LABEL[reason]) || null;

/** The export's `Status Gutschrift` cell: the on-screen label, with the reason for rows that cannot be paid. */
export const payoutExportStatus = (payout: ThgStationPayout | null | undefined): string => {
  if (!payout) return "";
  const reason = payoutReasonLabel(payout.state);
  const label = payoutStateLabel(payout);
  return reason ? `${label}: ${reason}` : label;
};

export type NoticeSeverity = "info" | "warn" | "error";

export interface PayoutNotice {
  severity: NoticeSeverity;
  summary: string;
  detail: string;
  /** The row acted on was out of date, so the year must be reloaded to show what is true now. */
  reload: boolean;
}

const withPeriod = (text: string) => (/[.!?]$/.test(text.trim()) ? text.trim() : `${text.trim()}.`);

/**
 * What to tell the operator after a refused action (research R3). A busy accounting system is a warning to try
 * again, never a failure; 404/409/422/502 mean the row was out of date (or a draft may be in recovery), so the
 * table is reloaded.
 */
export const payoutFailureNotice = (failure: ThgPayoutFailure, stationId: string | null): PayoutNotice => {
  const warn = (detail: string, reload: boolean): PayoutNotice => ({ severity: "warn", summary: "Warning", detail, reload });
  const error = (detail: string, reload: boolean): PayoutNotice => ({ severity: "error", summary: "Error", detail, reload });
  switch (failure.kind) {
    case "ACCOUNTING_BUSY":
      return { severity: "warn", summary: PAYOUT_COPY.busySummary, detail: PAYOUT_COPY.busy, reload: false };
    case "NOT_ELIGIBLE": {
      const reason = ineligibilityReasonLabel(failure.reason);
      return warn(reason && stationId ? PAYOUT_COPY.notEligible(stationId, reason) : failure.message, true);
    }
    case "CONFLICT":
      return warn(`${withPeriod(failure.message)} ${PAYOUT_COPY.reloaded}`, true);
    case "NOT_FOUND":
      return warn(stationId ? PAYOUT_COPY.notFound(stationId) : failure.message, true);
    case "ACCOUNTING_REJECTED":
      return error(PAYOUT_COPY.rejected(failure.message), true);
    case "FORBIDDEN":
      return error(PAYOUT_COPY.forbidden, false);
    default:
      return error(failure.message, false);
  }
};

export interface SyncNotice {
  severity: "info" | "warn";
  text: string;
}

export const syncResultNotice = (result: ThgPayoutSyncResult, year: number): SyncNotice => {
  if (result.checked === 0) return { severity: "info", text: PAYOUT_COPY.syncNothing(year) };
  const changes =
    result.finalized === 0 && result.cancelled === 0
      ? "no changes"
      : `${result.finalized} finalised there, ${result.cancelled} cancelled`;
  const text = `${PAYOUT_COPY.syncChecked(result.checked)}: ${changes}.`;
  if (result.missing > 0) {
    return { severity: "warn", text: `${text} ${PAYOUT_COPY.syncMissing(result.missing)}` };
  }
  return { severity: "info", text };
};
